<template>
  <main v-style:max-w="{ lg: '53.5rem' }">
    <h1 v-if="!titleBelowBreadcrumb" class="mb-6 subtitle-1">
      {{ heading.title }}
    </h1>
    <section v-if="data" class="container">
      <vf-tabs
        v-style:max-w="{ lg: '50rem' }"
        :active="activeTab"
        class="subtitle-5 mt-4"
        :tabs="data.tabs.map(({ category }) => category)"
        variant="left"
        @change="onTabChange"
      >
        <template v-for="(tab, i) in tabs" :key="i" #[`content-${i}`]>
          <vf-accordion
            v-for="(item, j) in tab.accordions"
            :key="item.id"
            v-style="{
              'm': { sm: j === 0 && '-0.1875rem 0 0' },
              'max-w': { lg: '62.5rem' },
            }"
            class="py-1 md:pl-0"
            :class="{ 'b-0': !i }"
            class-title="body-3 fw-normal"
            :open="activeAccordion"
            size="sm"
            :title="item.label"
            :value="`${j}`"
            variant="aligned"
            @click="toggleAccordion(item)"
          >
            <template v-for="(sizeTable, k) in item.sizeTables" :key="k">
              <div class="flex items-center between">
                <div class="justify-start pl-1">
                  <vf-switch
                    v-if="sizeTable.toggle !== undefined"
                    class="my-2"
                    :label-off="measureAbbreviation[toggleDefault.off]"
                    :label-on="measureAbbreviation[toggleDefault.on]"
                    :model-value="sizeTable.toggle"
                    @update:model-value="(v) => sizeTable.toggle = v"
                  />
                </div>
                <div class="px-3 text-right">
                  <vf-link
                    v-if="item.isMeasureVisible"
                    :model-value="!howToMeasure"
                    @click="scrollToElement(`howToMeasure-${i}-${j}`)"
                  >
                    {{ howToMeasure.labelLink }}
                  </vf-link>
                </div>
              </div>
              <h2 class="my-4 subtitle-3">
                {{ sizeTable.tableName }}
              </h2>
              <p v-if="sizeTable.descriptionAbove" class="my-4 text-xs">
                {{ sizeTable.descriptionAbove }}
              </p>
              <div
                class="mb-6 overflow-y-auto md:max-w-full"
                style="max-width: 80rem"
              >
                <vf-table
                  :aria-label="`${heading}: ${item.id}`"
                  class="ws-pre-wrap text-center text-sm"
                >
                  <tr>
                    <th
                      v-for="(sizeHeader, l) in sizeTable.sizeHeading"
                      :key="l"
                      class="b b-b-2 b-grey-60 bg-black px-2 py-5 c-white fw-bold"
                      :class="{ 'sticky left-0': !l }"
                      scope="col"
                      :style="{ 'min-width': l ? '6.75rem' : '7.75rem' }"
                    >
                      {{ sizeHeader }}
                    </th>
                  </tr>
                  <tr
                    v-for="(sizes, n) in sizeTable.sizes"
                    :key="n"
                    :class="{ 'bg-grey-90': n % 2 }"
                  >
                    <th
                      class="sticky left-0 b b-grey-60 px-2 py-5 fw-light"
                      :class="n % 2 ? 'bg-grey-90' : 'bg-white'"
                      scope="row"
                    >
                      <div>
                        {{ sizes.sizeIn ? sizes[sizeTable.toggle ? 'size' : 'sizeIn'] : sizes.size }}
                      </div>
                    </th>
                    <td
                      v-for="(size, p) in sizes.in ? sizes[sizeTable.toggle ? 'cm' : 'in'] : sizes.cm"
                      :key="p"
                      class="b b-grey-60 px-2 py-5"
                    >
                      {{ size }}
                    </td>
                  </tr>
                </vf-table>
                <p v-if="sizeTable.descriptionBelow" class="sticky left-0 my-4 text-xs" :class="sizeTable.descriptionBelowScrollMdHidden">
                  {{ sizeTable.descriptionBelow }}
                </p>
              </div>
            </template>
            <!-- How To Measure -->
            <template v-if="item.isMeasureVisible">
              <div class="my-8 b-t b-grey-60" :data-scroll-el="`howToMeasure-${i}-${j}`">
                <h2 class="my-4 pt-8 text-center subtitle-3 capitalize">
                  {{ howToMeasure.title }}
                </h2>
                <div class="my-10 f-col">
                  <template v-for="({ title, description }, q) in measurements.content" :key="q">
                    <p class="mb-4 text-sm">
                      <span class="subtitle-4">{{ title }}</span>: <span class="body-3 fw-normal">{{ description }}</span>
                    </p>
                  </template>
                  <base-picture v-bind="measurements.image" />
                </div>
                <p class="text-sm">
                  <span class="subtitle-4">{{ howToMeasure.returnsDescription }}</span>: <span class="body-3 fw-normal">{{ howToMeasure.returnsDescription2 }}
                    <span v-html="howToMeasure.returnsLink" /></span>
                </p>
              </div>
            </template>
          </vf-accordion>
        </template>
      </vf-tabs>
    </section>
    <!-- FAQ -->
    <section class="pt-12 container">
      <h2 class="mb-8 text-center subtitle-3">
        {{ faq.heading }}
      </h2>
      <div class="mb-10 f-col">
        <template v-for="({ question, answer }, key) in faq.questions" :key>
          <h3 class="mb-2 subtitle-3">
            {{ question }}
          </h3>
          <p v-html="answer" class="body-3 mb-8 ws-pre-wrap" />
        </template>
        <div class="flex justify-center b-b-1 b-b-grey-60 pb-8">
          <div v-for="(src, key) in faq.images" :key class="w-1/2 md:w-a md:px-8 <md:px-4">
            <base-picture v-bind="src" height="160" width="160" />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
const emit = defineEmits<{
  'change-page-title': [title: string]
}>()

const { titleBelowBreadcrumb } = useAppConfig().pages.help
const categoryQuery = useRouteQuery('category')
const productQuery = useRouteQuery('product')

const activeAccordion = ref('0')
const activeTab = ref('0')

// Function used for "category" and "product" queryString to highlight the proper accordion tab regardless of case provided.
const toLowerCase = (str: string) => str.toLowerCase()

const { data, error } = await useApi().internal.getStaticPage('help/size-charts')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { heading, measureAbbreviation, measurements, howToMeasure, tabs, faq } = data.value
const region = useRegion()

emit('change-page-title', heading.title)

const toggleDefault = {
  off: region === 'NORA' ? 'in' : 'cm',
  on: region === 'NORA' ? 'cm' : 'in'
}

activeTab.value = `${Math.max(data.value.tabs.findIndex(({ category }) =>
  typeof categoryQuery.value === 'string' && toLowerCase(categoryQuery.value) === toLowerCase(category)), 0)}`

activeAccordion.value = `${data.value.tabs[Math.max(+activeTab.value, 0)]?.accordions.findIndex(({ label }) =>
  typeof productQuery.value === 'string' && toLowerCase(productQuery.value) === toLowerCase(label))}`

const toggleAccordion = (item) => {
  if (typeof productQuery.value === 'string' && toLowerCase(productQuery.value) === toLowerCase(item.label))
    productQuery.value = null
  else
    productQuery.value = toLowerCase(item.label)
}

const onTabChange = (active: string) => {
  activeAccordion.value = ''
  productQuery.value = null
  if (data.value?.tabs[active]?.category)
    categoryQuery.value = toLowerCase(data.value.tabs[active].category)
}
</script>
